import { Theme, useMediaQuery } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ConvertIcon } from '@work4all/assets/icons/convert.svg';

import { useDialogs } from '@work4all/components';
import {
  ConvertableTypes,
  CreditNote,
} from '@work4all/components/lib/components/convert-popover/ConvertableTypes';
import { ConvertPopover } from '@work4all/components/lib/components/convert-popover/ConvertPopover';
import { CustomToolbarAction } from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';

import { usePermissions, useUser } from '@work4all/data';
import { getPopoverOrigin } from '@work4all/data/lib/hooks/usePopoverState';

import { Calculation } from '@work4all/models/lib/Classes/Calculation.entity';
import { Contract } from '@work4all/models/lib/Classes/Contract.entity';
import { DeliveryNote } from '@work4all/models/lib/Classes/DeliveryNote.entity';
import { Invoice } from '@work4all/models/lib/Classes/Invoice.entity';
import { Offer } from '@work4all/models/lib/Classes/Offer.entity';
import { Order } from '@work4all/models/lib/Classes/Order.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { canAddSalesOpportunities } from '@work4all/utils/lib/permissions';

import { useCanCloneDeliveryNote } from '../../../../hooks';
import { usePermissionsRights } from '../../../../hooks/use-permissions-rights';
import {
  CloneConvertDialogProps,
  ErpDialogConfirm,
  ErpObject,
  TargetEntityOptions,
} from '../types';

import { useCanFilterEntitiesByPermission } from './use-can-filter-entities-by-permission';
import { useCloneConvertDialog } from './use-clone-convert-dialog';
import { useConvertBzObjectMutation } from './use-convert-bz-object-mutation';
import { useConvertBzObjectToSalesOpportunity } from './use-convert-bz-object-to-sales-opportunity-mutation';

export type ConvertEntity =
  | Entities.order
  | Entities.inboundDeliveryNote
  | Entities.demand
  | Entities.offer
  | Entities.contract
  | Entities.calculation
  | Entities.deliveryNote
  | Entities.invoice
  | Entities.callMemo
  | Entities.note;

type EntityByConvertConfigs = {
  [key in ConvertEntity]: {
    allowedEntities: ConvertableTypes[];
    blockedEntities?: Entities[];
  };
};

export const ENTITY_BY_CONVERT_CONFIGS: EntityByConvertConfigs = {
  // supplier
  [Entities.demand]: {
    allowedEntities: [
      // Entities.order,
      Entities.inboundDeliveryNote,
      // Entities.inboundInvoice, // special case no 5. x -> inboundInvoice NOT NOW
    ],
  },
  [Entities.order]: {
    allowedEntities: [
      Entities.demand,
      Entities.inboundDeliveryNote, // special case no 4. order -> incomindDeliveryNote
      // Entities.inboundInvoice, // special case no 5. x -> inboundInvoice
    ],
  },
  [Entities.inboundDeliveryNote]: {
    allowedEntities: [
      Entities.demand,
      // Entities.order,
      // Entities.inboundInvoice, // special case no 5. x -> inboundInvoice
    ],
  },
  // customer
  [Entities.offer]: {
    allowedEntities: [
      Entities.salesOpportunities,
      Entities.contract,
      Entities.calculation,
      Entities.invoice,
      Entities.deliveryNote,
    ],
    blockedEntities: [
      Entities.order,
      Entities.demand,
      Entities.inboundDeliveryNote,
    ],
  },
  [Entities.contract]: {
    allowedEntities: [
      Entities.offer,
      Entities.calculation,
      Entities.invoice,
      Entities.deliveryNote, // special case no 1. contract -> deliveryNote
      Entities.order, // special case no 3. contract -> order
    ],
  },

  [Entities.calculation]: {
    allowedEntities: [
      Entities.contract,
      Entities.offer,
      Entities.deliveryNote,
      Entities.invoice,
    ],
  },

  [Entities.deliveryNote]: {
    allowedEntities: [
      Entities.offer,
      Entities.calculation,
      Entities.invoice,
      Entities.inboundDeliveryNote, // / special case no 3. deliveryNote -> inboundDeliveryNote
    ],
  },

  [Entities.invoice]: {
    allowedEntities: [
      Entities.offer,
      Entities.calculation,
      Entities.deliveryNote,
      Entities.contract,
      CreditNote,
      // Entities.credit special case no 2. invoice -> credit
    ],
  },

  // NOT ERP PART
  [Entities.callMemo]: {
    allowedEntities: [Entities.ticket, Entities.task],
  },
  [Entities.note]: {
    allowedEntities: [Entities.ticket, Entities.task],
  },
};

interface UseCloneConvertErpOptions {
  data?: (Order | Offer | Invoice | Contract | Calculation | DeliveryNote)[];
  entity: ConvertEntity;
}

function areDataValidForConversion(data?: UseCloneConvertErpOptions['data']) {
  if (!Array.isArray(data) || data.length === 0) {
    return false; // Handle empty or invalid input
  }

  const firstBpId = data[0]?.businessPartnerId;
  const firstCurrencyId = data[0]?.currencyId;
  const firstProjectId = data[0]?.projectId;
  return data.every(
    (item) =>
      item.businessPartnerId === firstBpId &&
      item.currencyId === firstCurrencyId &&
      item.projectId === firstProjectId &&
      !(item as Offer).isClosed
  );
}

export const useCloneConvertErpActions = (props: UseCloneConvertErpOptions) => {
  const { entity, data } = props;

  const isSmUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'));
  const user = useUser();

  const { t } = useTranslation();
  const [targetEntity, setTargetEntity] = useState<Entities>(null);
  const [targetEntityOptions, setTargetEntityOptions] = useState<
    TargetEntityOptions | undefined
  >(undefined);

  const { canConvert } = usePermissions();
  const canUserConvertRecord = useMemo(() => {
    return canConvert({ entity, withExtraInfos: true });
  }, [canConvert, entity]);

  const { mutate } = useConvertBzObjectMutation({ targetEntity });
  const mutateToSales = useConvertBzObjectToSalesOpportunity();
  const convertRootRef = useRef<HTMLButtonElement>(null);
  const dialogs = useDialogs();

  const convertOnClick = useCallback(
    async (selectedEntity: ConvertableTypes) => {
      if (selectedEntity === CreditNote) {
        setTargetEntity(Entities.invoice);
        setTargetEntityOptions({
          convertableTarget: CreditNote,
          invoiceAsCreditNote: true,
        });
      } else if (selectedEntity === Entities.salesOpportunities) {
        mutateToSales(entity, data as Offer[]);
      } else {
        setTargetEntity(selectedEntity);
      }
    },
    [data, entity, mutateToSales]
  );

  const { isErpLightUser } = usePermissionsRights();
  const { filterEntitiesByPermission } = useCanFilterEntitiesByPermission();

  const { convertTypes, disabledTypes } = useMemo(() => {
    // Full context: https://work4all.atlassian.net/browse/WW-6286
    if (isErpLightUser && entity === Entities.contract)
      return {
        convertTypes: [Entities.deliveryNote].filter(
          filterEntitiesByPermission
        ),
        disabledTypes: [],
      };

    const __types = ENTITY_BY_CONVERT_CONFIGS[entity]?.allowedEntities ?? [];
    const allowedTypes = [];
    const disabledTypesList = [];

    for (const x of __types) {
      if (x === Entities.salesOpportunities) {
        if (areDataValidForConversion(data) && canAddSalesOpportunities(user)) {
          allowedTypes.push(x);
        }
      } else if (x === CreditNote && data) {
        const isCreditNote = data.some(
          (invoice) =>
            invoice.value < 0 ||
            invoice.positionList?.some((pos) => pos.amount < 0)
        );

        // Conversion is allowed only if Invoice has no payment
        if (!isCreditNote && data.length === 1) {
          allowedTypes.push(x);
          // Check if invoice is in RA status
          const isInvoiceInRa = data.some(
            (invoice: Invoice) => invoice.ra === true
          );
          if (!isInvoiceInRa) {
            disabledTypesList.push(x);
          }
        }
      } else {
        allowedTypes.push(x);
      }
    }

    return {
      convertTypes: allowedTypes.filter(filterEntitiesByPermission),
      disabledTypes: disabledTypesList,
    };
  }, [isErpLightUser, entity, filterEntitiesByPermission, data, user]);

  const canCloneDeliveryNote = useCanCloneDeliveryNote();

  const isDeliveryNoteAndCloneable = useMemo(() => {
    return entity === Entities.deliveryNote && canCloneDeliveryNote;
  }, [entity, canCloneDeliveryNote]);

  const actions: CustomToolbarAction[] = useMemo((): CustomToolbarAction[] => {
    return [
      {
        actionKey: 'cloneOrConvert',
        IconComponent: ConvertIcon,
        title: t('COMMON.CONVERT'),
        disabled:
          // Full context: https://work4all.atlassian.net/browse/WW-7195
          !isDeliveryNoteAndCloneable &&
          !(canUserConvertRecord.allowed || convertTypes.length > 0),
        handler: (_id, event) => {
          convertRootRef.current = event?.currentTarget as HTMLButtonElement;
          const { id } = dialogs.open(
            (props) => (
              <ConvertPopover
                copyTypes={
                  data?.length === 1 &&
                  // Full context: https://work4all.atlassian.net/browse/WW-7195
                  (isDeliveryNoteAndCloneable ||
                    (!isErpLightUser && canUserConvertRecord.allowed))
                    ? [entity]
                    : undefined
                }
                convertTypes={convertTypes}
                disabledTypes={disabledTypes}
                onClick={(entity) => {
                  convertOnClick(entity);
                  dialogs.close(id);
                }}
                popoverState={{
                  open: props.open,
                  anchorEl: convertRootRef.current,
                  handleClick: () => {},
                  onClose: () => {
                    dialogs.close(id);
                  },
                  ...getPopoverOrigin(isSmUp ? 'bottom' : 'top'),
                }}
                convertTitle={t('COMMON.CONVERT_TO')}
                copyTitle={t('COMMON.COPY_CREATE')}
              />
            ),
            {}
          );
        },
        mode: 'Delete',
        sections: ['mobile', 'toolbar'],
        disableReason: canUserConvertRecord.allowed
          ? undefined
          : canUserConvertRecord.reason,
      },
    ];
  }, [
    t,
    canUserConvertRecord,
    dialogs,
    data?.length,
    isErpLightUser,
    entity,
    convertTypes,
    disabledTypes,
    isSmUp,
    convertOnClick,
    isDeliveryNoteAndCloneable,
  ]);

  const onClose = useCallback(() => {
    setTargetEntity(null);
  }, []);

  const onConfirm = useCallback(
    async (confirm: ErpDialogConfirm[]) => {
      onClose();
      const promises = confirm.map((singleConfirm) =>
        mutate({
          sourceEntity: entity,
          sourceIds: data.map((x) => x.id),
          targetEntity,
          ...singleConfirm,
        })
      );
      const response = await Promise.all(promises);
      const key =
        entity === Entities.contract && targetEntity === Entities.order
          ? 'COMMON.ERP.CONVERT.SUCCESS_CONTRACT'
          : 'COMMON.ERP.CONVERT.SUCCESS';
      if (response.flatMap((x) => x.errors).length === 0)
        await dialogs.alert({
          title: t('COMMON.INFO'),
          description:
            data.length > 1 || confirm.length > 1 ? t(key + '_MULTI') : t(key),
          closeLabel: t('COMMON.OK'),
        });
      else {
        await dialogs.alert({
          title: t('MASK.WARNING'),
          description: t('ERROR.COULD_NOT_REACH_SERVICE'),
          closeLabel: t('ALERTS.CLOSE'),
        });
      }
    },
    [data, dialogs, entity, mutate, onClose, targetEntity, t]
  );

  const dialogProps: Omit<CloneConvertDialogProps, 'open'> = useMemo(() => {
    return {
      onClose,
      onConfirm,
      sourceEntity: entity,
      targetEntity,
      data: data as ErpObject[],
      targetEntityOptions,
    };
  }, [data, entity, onClose, onConfirm, targetEntity, targetEntityOptions]);

  useCloneConvertDialog(dialogProps);

  return { actions };
};
